import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { catalogoMaestrosApi } from "@/plugins/axios/catalogo-maestros.client";
import type { AxiosResponse } from "axios";

interface MasterData {
  [key: string]: any;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

type ServiceConfig = {
  [key: string]: any;
};

const serviceMap: Record<
  string,
  (config?: any, signal?: AbortSignal) => Promise<{ data: any }>
> = {
  // Servicios que usan filtros
  sociedades: async (config, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/sociedades", {
      params: config?.filters ?? {},
      signal,
    });
    return { data: response.data };
  },
  productos: async (config, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/productos", {
      params: config?.filters ?? {},
      signal,
    });
    return { data: response.data };
  },
  laboratorios: async (config, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/laboratorios", {
      params: config?.filters ?? {},
      signal,
    });
    return { data: response.data };
  },
  aprobadores: async (config, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/aprobadores", {
      params: config?.filters ?? {},
      signal,
    });
    return { data: response.data };
  },

  // Servicios que no usan filtros
  grupos: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/grupos", { signal });
    return { data: response.data };
  },
  escalas: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/escalas", { signal });
    return { data: response.data };
  },
  descuentos: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/descuentos", { signal });
    return { data: response.data };
  },
  tipo_descuentos: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/tipo-descuentos", { signal });
    return { data: response.data };
  },
  bases: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/bases", { signal });
    return { data: response.data };
  },
  grupo_descuentos: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/grupo-descuentos", { signal });
    return { data: response.data };
  },
  beneficiarios: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/beneficiarios", { signal });
    return { data: response.data };
  },
  tipo_base: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/tipo-bases", { signal });
    return { data: response.data };
  },
  tipo_rechazos: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/tipo-rechazos", { signal });
    return { data: response.data };
  },
  estados_negociacion: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/estados-negociacion", { signal });
    return { data: response.data };
  },
  periodos: async (_, signal) => {
    const response: AxiosResponse = await catalogoMaestrosApi.get("/periodos", { signal });
    return { data: response.data };
  },
};

const MasterDataContext = createContext<MasterData | undefined>(undefined);

export const MasterDataProvider = ({
  children,
  requiredServices = [],
  serviceConfig = {},
}: {
  children: React.ReactNode;
  requiredServices?: string[];
  serviceConfig?: ServiceConfig;
}) => {
  const [data, setData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = async (signal?: AbortSignal) => {
    try {
      setLoading(true);

      const results = await Promise.all(
        requiredServices.map((key) => {
          const config = serviceConfig.hasOwnProperty(key)
            ? serviceConfig[key]
            : undefined;

          return serviceMap[key]?.(config, signal);
        })
      );

      const newData: Record<string, any> = {};
      requiredServices.forEach((key, index) => {
        const result = results[index];
        newData[key] = result?.data ?? result;
      });

      // Solo actualiza si los datos realmente cambiaron
      setData((prevData) => {
        const isEqual = JSON.stringify(prevData) === JSON.stringify(newData);
        return isEqual ? prevData : newData;
      });
    } catch (err) {
      if (!axios.isCancel(err)) {
        setError(err as Error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => {
      controller.abort();
    };
  }, [JSON.stringify(requiredServices), JSON.stringify(serviceConfig)]);

  const value: MasterData = {
    ...data,
    loading,
    error,
    refresh: () => {
      const controller = new AbortController();
      loadData(controller.signal);
    },
  };

  return (
    <MasterDataContext.Provider value={value}>
      {children}
    </MasterDataContext.Provider>
  );
};

export const useMasterData = () => {
  const context = useContext(MasterDataContext);
  if (!context) {
    throw new Error("useMasterData must be used within a MasterDataProvider");
  }
  return context;
};
